import { useEffect, useRef, useState } from 'react';
import { hasAtLeastNJpdProjects } from '@jira/polaris__lib-jpd-projects-service/src/hasAtLeastNJpdProjects.tsx';

// Module-level cache so the network call is made at most once per page load.
let cachedResult: boolean | undefined;

/**
 * Returns true only when we can confirm that the tenant has at least 2 JPD spaces.
 *
 * Implementation detail: fetch only 2 results from the existing JPD project search endpoint
 * instead of paging through all spaces.
 */
export const useHasAtLeastTwoJpdSpaces = (): boolean | undefined => {
	const [hasAtLeastTwo, setHasAtLeastTwo] = useState<boolean | undefined>(cachedResult);
	const fetchFn = useRef(hasAtLeastNJpdProjects);
	// oxlint-disable-next-line react/react-compiler
	fetchFn.current = hasAtLeastNJpdProjects;

	useEffect(() => {
		if (cachedResult !== undefined) {
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const result = await fetchFn.current(2);
				if (!cancelled) {
					cachedResult = result;
					setHasAtLeastTwo(result);
				}
			} catch {
				// fail closed
				if (!cancelled) {
					cachedResult = false;
					setHasAtLeastTwo(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	return hasAtLeastTwo;
};

// exported for tests only
export const resetHasAtLeastTwoJpdSpacesCache = (): void => {
	cachedResult = undefined;
};
